import { LeafTypes } from 'shared/types/ingestion'
import { ILeaf, INode } from 'shared/types/tree'
import { allLeavesOfType } from '../utility/TreeHelpers'
import { getAPIRequest } from 'infrastructure/api'
import {
  IdType,
  mapStandardIdsToStandards,
  standardRequest,
} from 'shared/hooks/standards/hooks'
import { Standard, StandardsMap } from 'shared/types'
import { makeKiddomPostRequest } from 'infrastructure/api'
import { makeContentServiceGetRequest } from 'infrastructure/contentService'

type ApiStandard = {
  standard_id: number
  standard_name: string
  standard_description: string
}

export function normalizeStandardName(standardName: string) {
  return standardName
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .toUpperCase()
    .trim()
}

export function addStandards(standards: Standard[]) {
  return makeKiddomPostRequest({
    kiddomPath: '/api/v2/standards/add',
    requestBody: {
      standards: standards.map((s: Standard) => {
        return {
          standard_name: s.standardName,
          standard_description: s.standardDescription,
          grade_id: s.gradeId,
          state: s.state,
          subject_id: s.subjectId,
          adoption_date: s.adoptionDate,
        }
      }),
    },
  })
}

export function updateStandards(updatedStandards: Standard[]) {
  return makeKiddomPostRequest({
    kiddomPath: `/api/v2/standards/update`,
    requestBody: {
      standards: updatedStandards.map((a: Standard) => {
        return {
          standard_id: a.standardId,
          standard_name: a.standardName,
          standard_description: a.standardDescription,
          grade_id: a.gradeId,
          state: a.state,
          subject_id: a.subjectId,
        }
      }),
    },
  })
}

export async function getStandardsMapFromIds(
  standardIds: number[],
): Promise<Record<number, string>> {
  const { result } = await makeKiddomPostRequest<{ standards: ApiStandard[] }>({
    kiddomPath: `/api/v1/standards`,
    requestBody: { IDs: standardIds },
  })

  if (!result) return {}

  return result.standards.reduce((acc, s) => {
    return {
      ...acc,
      [s.standard_id]: s.standard_name,
    }
  }, {})
}

export async function getStateStandardsAsMap(
  standardState: string,
): Promise<Record<string, Standard>> {
  const stateStandards = await getAPIRequest<Standard[]>(
    `/api/v2/standards?state=${standardState}&unlimited=true`,
  )

  return stateStandards.reduce(
    (acc: Record<string, Standard>, s: Standard) => {
      acc[normalizeStandardName(s.standardName)] = s
      return acc
    },
    {} as Record<string, Standard>,
  )
}

export async function getStandardsMapFromNames(
  standardNames: string[],
  params: { [key: string]: string } = {},
): Promise<{ standardsMap: { [key: string]: Standard }; errors: string[] }> {
  const standardsMap: { [key: string]: Standard } = {}
  const errors: string[] = []

  const paramsString = Object.keys(params).reduce((acc, key) => {
    return `${acc}&${key}=${params[key]}`
  }, '')

  const requests = standardNames.map(async (s) => {
    try {
      const standards = await getAPIRequest<Standard[]>(
        `/api/v2/standards?standard_name=${s}${paramsString}`,
      )
      if (standards.length === 0) {
        // Fallback to search if the standard is not found by name
        const searchedStandards = await getAPIRequest<Standard[]>(
          `/api/v2/standards?search=${s}${paramsString}`,
        )
        if (searchedStandards.length === 0) {
          errors.push(`Standard not found: ${s}`)
        } else {
          standardsMap[s] = searchedStandards[0]
        }
      } else {
        standardsMap[s] = standards[0]
      }
    } catch {
      errors.push(`API request failed for ${s}`)
    }
  })

  // Wait for all the API requests to complete
  await Promise.all(requests)

  return {
    standardsMap,
    errors,
  }
}

/** Content service kiddom standards response item (snake_case from co-hermes). */
type ContentServiceStandardItem = {
  standard_id: number
  standard_name: string
  standard_description: string
  grade_id: number
  subject_id: number
  state: string
  adoption_date: number
  is_common_core?: boolean
  luser_id?: number
}

function contentServiceStandardToStandard(
  item: ContentServiceStandardItem,
): Standard {
  return {
    standardId: item.standard_id,
    standardName: item.standard_name,
    standardDescription: item.standard_description ?? '',
    gradeId: item.grade_id,
    subjectId: item.subject_id,
    state: item.state ?? null,
    adoptionDate: item.adoption_date ?? 0,
    isCommonCore: item.is_common_core,
    luserId: item.luser_id != null ? String(item.luser_id) : undefined,
  }
}

/**
 * Normalize standard name for Replace Standards lookup: alphanumeric only, uppercase.
 * Matches replaceStandards/utilities normalize (no spaces in standard codes per tool rules).
 */
export function normalizeStandardNameForReplace(standardName: string): string {
  return standardName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
}

/**
 * Fetch all standards for a state from the content service and return a map from normalized name to Standard.
 * One API call; frontend has full state standards to resolve replacement names (exact match via normalized key).
 */
export async function getStateStandardsMapFromContentService(
  state: string,
): Promise<{ standardsMap: { [key: string]: Standard }; errors: string[] }> {
  const standardsMap: { [key: string]: Standard } = {}
  const errors: string[] = []

  try {
    const path = `content/v1/standards/kiddom?state=${encodeURIComponent(state)}`
    const response = await makeContentServiceGetRequest(path)
    const json = await response.json()

    if (!response.ok) {
      errors.push(`API request failed for state ${state}`)
      return { standardsMap, errors }
    }

    const data = json?.data ?? []
    if (!Array.isArray(data)) {
      return { standardsMap, errors }
    }

    for (const item of data as ContentServiceStandardItem[]) {
      const standard = contentServiceStandardToStandard(item)
      const key = normalizeStandardNameForReplace(item.standard_name)
      if (!(key in standardsMap)) {
        standardsMap[key] = standard
      }
    }
  } catch {
    errors.push(`API request failed for state ${state}`)
  }

  return {
    standardsMap,
    errors,
  }
}

function getKiddomSubjectFromEdgateSubject(subj: string) {
  switch (subj) {
    case 'Mathematics':
      return 'Math'
    case 'Language Arts':
      return 'English'
    case 'Social and Emotional Learning':
      return 'Social Emotional Learning'
    default:
      return subj
  }
}

interface ParsedStandard {
  state: string
  gradeId: number
  subjectId: number
  standardName: string
  standardDescription: string
  adoptionDate: number
  previousDescription?: string
  standardId?: number
}

export class StandardsIngestion {
  newStandards: Promise<ParsedStandard[]>
  stateStandards: Promise<{ [state: string]: Standard[] }>
  subjToCode: Promise<{ [key: string]: number }>
  gradeToCode: { [key: string]: number }
  setIngestionProgress: (progress: number) => void

  constructor(file: File, setIngestionProgress: (progress: number) => void) {
    this.stateStandards = this.getStateStandards()
    this.subjToCode = this.getSubjects()
    this.newStandards = this.parseNewStandards(file)
    this.gradeToCode = {
      K: 1,
      '1': 2,
      '2': 3,
      '3': 4,
      '4': 5,
      '5': 6,
      '6': 7,
      '7': 8,
      '8': 9,
      '9': 10,
      '10': 11,
      '11': 12,
      '12': 13,
    }
    this.setIngestionProgress = setIngestionProgress
  }

  async getSubjects() {
    const subjects = (
      await getAPIRequest<{
        subjects: { subject_name: string; subject_id: number }[]
      }>('/api/subjects')
    ).subjects
    return subjects.reduce<Record<string, number>>(
      (acc: { [key: string]: number }, s) => {
        acc[s.subject_name] = s.subject_id
        return acc
      },
      {},
    )
  }

  async getStateStandards() {
    const stateToStandards: { [state: string]: Standard[] } = {}

    const states = (
      await getAPIRequest<{ abbreviation: string }[]>(
        '/api/v2/standards/states',
      )
    ).map((s) => s.abbreviation)
    let currentState = 0
    for (const state of states) {
      currentState += 1
      stateToStandards[state] = await standardRequest({
        state: state,
        unlimited: true,
      })
      this.setIngestionProgress(currentState / states.length / 2)
    }

    return stateToStandards
  }

  async parseNewStandards(file: File) {
    const subjCodes = await this.subjToCode
    const fileText = await file.text()

    return fileText
      .split('\n')
      .map((l) => {
        const splitLine = l.split('	')
        const [
          state,
          grade,
          subject,
          _,
          __,
          ___,
          ____,
          name,
          description,
          adoptionDate,
        ] = splitLine
        if (!name) return null
        if (name.trim() === '') return null
        return {
          state,
          gradeId: this.gradeToCode[grade],
          subjectId: subjCodes[getKiddomSubjectFromEdgateSubject(subject)],
          standardName: name?.trim().replace(/^\.|\.$/g, ''),
          standardDescription: description,
          adoptionDate: parseInt(adoptionDate),
        }
      })
      .filter((s): s is ParsedStandard => s !== null)
  }

  async getUpdatedAndNewStandards() {
    const newStandards = await this.newStandards
    const stateStandards = await this.stateStandards

    const updatedStandards: Standard[] = []
    const addedStandards: Standard[] = []

    let currS = 0

    const delay = () => new Promise((resolve) => setTimeout(resolve, 0))
    while (currS < newStandards.length) {
      newStandards.slice(currS, currS + 1000).forEach((ns) => {
        const currStateStandards = stateStandards[ns.state]
        if (!currStateStandards) return
        const returnedfilter = currStateStandards.filter((ss) => {
          return (
            ss.standardName === ns.standardName &&
            ss.gradeId === ns.gradeId &&
            ss.subjectId === ns.subjectId
          )
        })

        if (returnedfilter.length === 1) {
          if (
            returnedfilter[0].standardDescription !== ns.standardDescription
          ) {
            ns.previousDescription = returnedfilter[0].standardDescription
            updatedStandards.push({
              ...ns,
              standardId: returnedfilter[0].standardId,
            })
          }
        }

        if (returnedfilter.length === 0) {
          addedStandards.push(ns as Standard)
        }
      })
      this.setIngestionProgress(0.5 + currS / newStandards.length / 2)
      await delay()
      currS += 1000
    }

    return [updatedStandards, addedStandards]
  }
}
export class TreeStandards {
  tree: INode
  skillLeaves: ILeaf[]
  allStandards: Standard[]
  standardsMap: StandardsMap
  userGenerated: Standard[]
  userGeneratedLeaves: ILeaf[]
  countUserGenerated: number
  stateStandards: { [key: string]: Standard[] }
  errors: { [key: IdType]: string } = {}

  constructor(
    tree: INode,
    standards: StandardsMap,
    errors: { [key: IdType]: string },
    leaves: ILeaf[] = [],
  ) {
    this.tree = tree
    this.skillLeaves = leaves
    this.standardsMap = standards
    this.errors = errors
    this.allStandards = this.getAllStandards()
    this.userGenerated = this.getUserGeneratedStandards()
    this.userGeneratedLeaves = this.getUserGeneratedStandardLeaves()
    this.countUserGenerated = this.getUserGeneratedStandardsCount()
    this.stateStandards = this.getStateStandards()
    return this
  }

  static async create(tree: INode): Promise<TreeStandards> {
    const leaves = allLeavesOfType(
      tree,
      LeafTypes.Skill,
      LeafTypes.WeightedSkill,
    )
    const ids = leaves.map((l) => l.body.skillID)
    const { standards, errors } = await mapStandardIdsToStandards(ids)
    return new TreeStandards(tree, standards, errors, leaves)
  }

  getAllStandards() {
    if (!this.standardsMap) return []
    return Object.values(this.standardsMap)
  }

  getUserGeneratedStandards() {
    return this.allStandards.filter((s) => s.luserId)
  }

  getStandardsErrors() {
    return this.errors
  }

  getLeavesWithStandardErrors() {
    return this.skillLeaves.filter((l) => {
      return this.errors[l.body.skillID]
    })
  }

  getUserGeneratedStandardLeaves() {
    const userGeneratedStandardIds = this.userGenerated.map((s) => s.standardId)
    return this.skillLeaves.filter((l) => {
      return userGeneratedStandardIds.includes(l.body.skillID)
    })
  }

  getUserGeneratedStandardsCount() {
    return this.userGeneratedLeaves.length
  }

  getStateStandards() {
    return this.allStandards.reduce(
      (acc, s) => {
        if (s.state) {
          if (acc[s.state]) {
            acc[s.state].push(s)
          } else {
            acc[s.state] = [s]
          }
        }
        return acc
      },
      {} as { [key: string]: Standard[] },
    )
  }

  stateStandardsCount() {
    const stateStandards = this.stateStandards
    return Object.keys(stateStandards).reduce(
      (acc, s) => {
        acc[s] = stateStandards[s].length
        return acc
      },
      {} as { [key: string]: number },
    )
  }
}
